import * as CL from '@design-system/component-library';
import { useDispatch } from 'react-redux';
import { EcommerceEventTypeV4, pushSubscriptionModifyEventToDataLayer } from '../../common/analytics.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { DialogType } from '../../common/enums.js';
import { cancelMsg, confirmMsg, editAddonsMsg, t } from '../../common/i18n/index.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { changeSubscriptionAddons } from '../../selfservice/actions/index.js';
import { pushAnalyticsEvent } from '../PbxAccordionContent/PbxAccordionUtils';
import { AddOnChangeConfirmationDialogContent } from '../SubscriptionDetails/AddOnChangeConfirmationDialogContent.js';
import {
  calculateServiceFee,
  calculateUpdatedPrice,
  getAddonAttributes,
} from '../SubscriptionDetails/addOnDependencyUtils.js';
import { useAnalyticsId } from './addOnAnalyticsUtils.js';
import type { CategoryKey } from '../../common/utils/categoryUtils.js';
import type { AddOn } from '../../generated/api/addOn.js';
import type { Subscription } from '../../generated/api/subscription.js';

import './AddOnManagementButtons.scss';

interface AddOnManagementButtonsProps {
  showEditView: boolean;
  resetEditView: () => void;
  setShowEditView: (show: boolean) => void;
  shouldDisableConfirm: boolean;
  addedAddOns: AddOn[];
  removedAddOns: AddOn[];
  subscription: Subscription;
  category: CategoryKey;
  isEmployee: boolean;
  changeOfferAllowed: boolean;
  updatedAddOns: AddOn[];
  selectedLanguage?: string;
}

const EditButtons = ({
  resetEditView,
  shouldDisableConfirm,
  addedAddOns,
  removedAddOns,
  subscription,
  category,
  isEmployee,
  updatedAddOns,
  selectedLanguage,
}: AddOnManagementButtonsProps) => {
  const dispatch = useDispatch();
  const analyticsBundleId = useAnalyticsId();

  return (
    <div className="of-addon-management-buttons__edit">
      <CL.Button
        className={dsClass.MARGIN_RIGHT_2}
        onClick={() => {
          const addOnCodesToAdd = addedAddOns.map(addOn => addOn.addOnCode);
          const finalRemovalCodes = removedAddOns.map(addOn => addOn.addOnCode);
          const updatedAddOnCodes = updatedAddOns.map(addOn => addOn.addOnCode);
          const addonAttributes = getAddonAttributes([...addOnCodesToAdd, ...updatedAddOnCodes], selectedLanguage);
          const updatedPrice = calculateUpdatedPrice(addedAddOns, removedAddOns, category, subscription);

          pushSubscriptionModifyEventToDataLayer('addons');
          pushAnalyticsEvent(addedAddOns, EcommerceEventTypeV4.ADD_TO_CART, analyticsBundleId);

          openDialog({
            type: DialogType.GENERIC_CONFIRMATION_DIALOG,
            header: t.LYSP('Service changes'),
            onConfirm: () => {
              dispatch(
                changeSubscriptionAddons(
                  subscription,
                  addOnCodesToAdd,
                  finalRemovalCodes,
                  updatedAddOnCodes,
                  addonAttributes
                )
              );
              pushAnalyticsEvent(addedAddOns, EcommerceEventTypeV4.PURCHASE, analyticsBundleId);
              pushAnalyticsEvent(removedAddOns, EcommerceEventTypeV4.REMOVE_ADDITIONAL_SERVICES, analyticsBundleId);
              resetEditView();
            },
            body: (
              <AddOnChangeConfirmationDialogContent
                addedAddOns={addedAddOns}
                removedAddOns={removedAddOns}
                updatedPrice={updatedPrice}
                serviceFee={calculateServiceFee(addedAddOns)}
                isEmployee={isEmployee}
              />
            ),
            disableConfirm: updatedPrice === undefined,
          });
        }}
        disabled={shouldDisableConfirm}
      >
        {t.QVYK(confirmMsg)}
      </CL.Button>
      <CL.Button color="link" onClick={resetEditView}>
        {t.B2V1(cancelMsg)}
      </CL.Button>
    </div>
  );
};

const ViewButton = ({ changeOfferAllowed, setShowEditView }: AddOnManagementButtonsProps) => {
  return (
    <div className="of-addon-management-buttons__view">
      <CL.Button
        color="light"
        onClick={() => {
          if (changeOfferAllowed) {
            setShowEditView(true);
          } else {
            openDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
          }
        }}
      >
        {t.BG4W(editAddonsMsg)}
      </CL.Button>
    </div>
  );
};

export const AddOnManagementButtons = (props: AddOnManagementButtonsProps) => {
  const { showEditView } = props;

  return (
    <div className="of-addon-management-buttons">
      {showEditView ? <EditButtons {...props} /> : <ViewButton {...props} />}
    </div>
  );
};
